import cloneDeep from 'lodash/cloneDeep';
import { Signal } from 'typed-signals';
import type {
  BlockerModel,
  BlockerState,
  BlockerType,
  BirdHouseBlockerState,
  BoardScheduler,
  BlockerMatchParams,
} from '../../types';
import type { TileStackModel } from '../board/TileStackModel';
import { LevelTaskType } from '../../../tasks/types';
import type { TasksRunner } from '../../../../libs/TasksRunner';

let instanceIndex = 0;

export class BirdHouseBlockerModel implements BlockerModel {
  public readonly underTile = false;

  // eslint-disable-next-line no-plusplus
  public readonly id = `birdHouse_${instanceIndex++}`;

  public readonly destructible = false;

  public readonly onStartReleaseAnimation = new Signal();

  public readonly onStartFullAnimation = new Signal();

  public readonly onReleaseCollectable = new Signal();

  public readonly onTaskComplete = new Signal();

  private levelTaskComplete = false;

  private readonly state: BirdHouseBlockerState;

  private sequenceIsRunning = false;

  private readonly queue: TileStackModel[] = [];

  constructor(
    private readonly stack: TileStackModel,
    private readonly scheduler: BoardScheduler,
    private readonly taskRunner: TasksRunner,
    state: BirdHouseBlockerState,
  ) {
    this.state = cloneDeep(state);
  }

  public onMatch(params: BlockerMatchParams): void {
    const { stack, distantMatch } = params;
    if (!this.stack.isNeighborOf(stack) && !distantMatch) return;

    if (!this.sequenceIsRunning) {
      this.taskRunner.addTask(this.runBirdHouseSequence());
    } else if (!this.levelTaskComplete) {
      this.queue.push(stack);
    }
  }

  public onLevelTaskComplete(taskType: LevelTaskType): void {
    if (taskType === LevelTaskType.Birds) {
      this.levelTaskComplete = true;

      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const model = this;
      this.taskRunner.addTask(
        (function* emitTaskComplete(): Generator<void, void> {
          while (model.sequenceIsRunning) {
            yield;
          }
          model.onTaskComplete.emit();
        })(),
      );
    }
  }

  private *runBirdHouseSequence(): Generator<void, void> {
    const { levelTaskComplete: taskComplete } = this;

    this.sequenceIsRunning = true;

    if (!taskComplete) {
      // Release bird sequence.
      this.onStartReleaseAnimation.emit();
      yield* this.scheduler.waitGenerator(null, 150);
      this.onReleaseCollectable.emit();
      yield* this.scheduler.waitGenerator(null, 500);
    } else {
      // Full sequence.
      this.onStartFullAnimation.emit();
      yield* this.scheduler.waitGenerator(null, 4100);
    }

    // Check queue to see if any more birds needs to be released.
    if (this.queue.length > 0) {
      this.queue.shift();
      yield* this.runBirdHouseSequence();
    } else {
      this.sequenceIsRunning = false;
    }
  }

  public getType(): BlockerType {
    return this.state.type;
  }

  public exportState(): BlockerState {
    return this.state;
  }
}
